/**
 * Compute badge number labels for a list of exercises, accounting for combo set groups.
 *
 * Individual exercises get sequential numbers (1, 2, 3...).
 * Combo set members get groupNumber + letter (1a, 1b, 2a, 2b...).
 * Each combo set occupies one number slot; each individual exercise occupies one number slot.
 */
export function computeExerciseBadgeNumbers(items: { comboSetNumber?: number | undefined }[]): string[] {
  if (items.length === 0) return [];

  const hasAnyCombos = items.some(e => e.comboSetNumber !== undefined);
  if (!hasAnyCombos) {
    return items.map((_, i) => (i + 1).toString());
  }

  const result: string[] = [];
  let groupNumber = 0;
  const seenComboSets = new Set<number>();
  const comboGroupNum = new Map<number, number>();
  const comboCount = new Map<number, number>();
  for (const e of items) {
    // if individual exercise, assign its own number slot
    if (e.comboSetNumber === undefined) {
      groupNumber++;
      result.push(groupNumber.toString());
      continue;
    }

    // if first exercise in a new combo set, assign a new group number
    if (!seenComboSets.has(e.comboSetNumber)) {
      groupNumber++;
      seenComboSets.add(e.comboSetNumber);
      comboGroupNum.set(e.comboSetNumber, groupNumber);
      comboCount.set(e.comboSetNumber, 0);
    }

    // append letter suffix for position within the combo set (a, b, c...)
    const count = comboCount.get(e.comboSetNumber)! + 1;
    comboCount.set(e.comboSetNumber, count);
    result.push(`${comboGroupNum.get(e.comboSetNumber)}${String.fromCharCode(96 + count)}`);
  }
  return result;
}
